import { API_BASE_URL } from '../config';
import { noteCoachRequest } from './coachSequenceTiming';

// Diagnostics never retain configs, URLs, bodies, identities or Error objects.
const clock = () => performance.now();
const records = [];
let consoleRecords = 0;
const snapshot = () => records.map(record => ({ ...record, serverTiming: record.serverTiming.map(stage => ({ ...stage })) }));
const clear = () => { records.length = 0; consoleRecords = 0; };
const publish = (record, emitConsole) => {
  if (records.length === 100) records.shift();
  records.push(record);
  if (typeof window !== 'undefined') window.katafitRequestDiagnostics = { snapshot, clear };
  noteCoachRequest(record);
  if (emitConsole && consoleRecords < 100) {
    consoleRecords += 1;
    console.info('[katafit-request-performance]', snapshot()[records.length - 1]);
  }
};
const milliseconds = value => Math.round(Math.max(0, value) * 10) / 10;
const timingNames = new Set(['total', 'auth', 'handler', 'feed_profile', 'feed_existence', 'feed_source', 'feed_tail', 'feed_extras', 'feed_count', 'feed_oldest', 'feed_metadata', 'feed_hydration', 'feed_meals', 'feed_weights', 'feed_targets', 'feed_social', 'feed_status', 'feed_has_more', 'history_container', 'history_authority', 'history_metadata', 'history_source', 'history_enrichment', 'history_weights', 'workout_create', 'workout_gym', 'workout_start', 'workout_lifecycle', 'profile_read', 'profile_backfill', 'profile_enrichment']);

for (const name of ['meal_food_lookup', 'meal_evaluation', 'calorie_candidates', 'calorie_metadata', 'calorie_evaluation', 'targets_prescribed', 'targets_formula_enrichment', 'weight_cursor_next', 'weight_evaluation']) timingNames.add(name);

// Coach sequence boundaries; mirrors the backend fixed stage allowlist.
for (const name of ['set_lookup', 'set_write', 'set_audit', 'set_thread_sync', 'set_gym', 'set_calories', 'completion_write', 'completion_lifecycle', 'completion_timeline', 'completion_stream_stop',
  'status_replay', 'status_record', 'recommendation_enqueue', 'recommendation_status', 'recommendation_generate', 'recommendation_resume', 'recommendation_canonical', 'position_write', 'daily_recommendation_read', 'mcp_catalog', 'mcp_handle', 'coach_home_read']) timingNames.add(name);

for (const name of ['thread_authority', 'thread_indexes', 'thread_workout', 'thread_start_resume', 'thread_exercise_resume', 'thread_projection']) timingNames.add(name);

// `boundary` names when finishRequestPerformance runs: fetch resolves at
// response headers; Axios interceptors run after the body is read and decoded;
// a few callers finish only after consuming the body and their own handling.
const BOUNDARIES = new Set(['response_headers', 'response_decoded', 'after_body_handling']);
export function beginRequestPerformance(config, boundary = 'response_headers') {
  try {
    const api = new URL(API_BASE_URL || window.location.origin);
    const target = new URL(config.url, api);
    if (target.origin !== api.origin) return null;
    const method = (config.method || 'get').toLowerCase();
    const path = target.pathname;
    let route;
    if (method === 'get' && path === '/api/activities') route = config.params?.scope === 'today-actions' ? 'today-actions' : 'activities';
    else if (method === 'get' && /^\/api\/workout_exercises\/[^/]+\/(history|weight_history)$/.test(path)) route = path.endsWith('/weight_history') ? 'exercise-weight-history' : 'exercise-history';
    else if (method === 'get' && /^\/api\/workouts\/[^/]+\/coach-thread\/?$/.test(path)) route = 'workout-coach-thread';
    else if (method === 'put' && /^\/api\/activities\/[^/]+\/start$/.test(path)) route = 'workout-start';
    else if (method === 'get' && /^\/api\/friends\/feed\/(friends|dojo|presence)$/.test(path)) route = 'friends-feed';
    else if (method === 'get' && (path === '/api/user' || /^\/api\/user\/(affiliation|activity_stats|compliance|badge_stats)$/.test(path))) route = 'user-profile';
    else if (method === 'post' && path === '/api/auth/clerk/sync') route = 'auth-sync';
    else if (method === 'put' && /^\/api\/workout_sets\/[^/]+\/?$/.test(path)) route = 'workout-set-save';
    else if (/^\/api\/workouts\/[^/]+\/recommendations\/[^/]+\/?$/.test(path) && (method === 'get' || method === 'post')) route = `exercise-recommendation-${method}`;
    else if (method === 'put' && /^\/api\/activities\/[^/]+\/complete\/?$/.test(path)) route = 'activity-complete';
    else if (method === 'put' && /^\/api\/activities\/[^/]+\/position\/?$/.test(path)) route = 'activity-position';
    else if (method === 'post' && path === '/api/status') route = 'status-save';
    else if (method === 'get' && path === '/api/recommendations/latest') route = 'daily-recommendation';
    else if (method === 'get' && path === '/api/coach/home') route = 'coach-home';
    if (!route) return null;
    return { route, boundary: BOUNDARIES.has(boundary) ? boundary : 'unspecified', started: clock(), authEnded: null, finished: false };
  } catch (_) { return null; }
}

export function endRequestAuth(state) {
  try { if (state) state.authEnded = clock(); } catch (_) { /* Observational only. */ }
}

export function finishRequestPerformance(state, response, error = null) {
  try {
    if (!state || state.finished) return;
    state.finished = true;
    const ended = clock();
    const totalMs = milliseconds(ended - state.started);
    const headers = response?.headers;
    const header = name => typeof headers?.get === 'function' ? headers.get(name) : headers?.[name];
    const rawId = header('x-request-id');
    const requestId = typeof rawId === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(rawId) ? rawId : null;
    const rawTiming = header('server-timing');
    const serverTiming = typeof rawTiming === 'string' ? rawTiming.slice(0, 2048).split(',').slice(0, 32).flatMap(part => {
      const match = /^\s*([a-z_]+);dur=(\d+(?:\.\d+)?)(?:\s*;.*)?\s*$/.exec(part);
      const durationMs = match && Number(match[2]);
      return match && timingNames.has(match[1]) && Number.isFinite(durationMs) && durationMs <= 3600000 ? [{ name: match[1], durationMs }] : [];
    }).slice(0, 32) : [];
    const status = response?.status;
    publish({
      route: state.route, boundary: state.boundary, outcome: error ? (state.authEnded === null ? 'auth-failure' : 'transport-failure') : 'success',
      startMs: milliseconds(state.started), endMs: milliseconds(ended),
      totalMs, authMs: milliseconds((state.authEnded ?? ended) - state.started),
      transportMs: state.authEnded === null ? 0 : milliseconds(ended - state.authEnded),
      status: Number.isInteger(status) && status >= 100 && status <= 599 ? status : null,
      requestId, serverTiming,
    }, Boolean(error) || totalMs >= 500);
  } catch (_) { /* Diagnostics must never change request settlement. */ }
}
